import {
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, filter } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { StorageService } from 'src/app/storage.service';
import { CourseDisplayerService } from 'src/app/views/displayer/courses/course-displayer/course-displayer.service';
import { environment } from 'src/environments/environment';
import { ContentStateService } from '../content-state.service';
import { Chunk } from './chunk.dto';
import { ChunkService, ChunkStateCommand } from './chunk.service';

export interface AudioActionEvent {
  action: 'play' | 'pause';
  chunkId: string;
  chunk?: any;
  currentTime?: number;
}

@Component({
  selector: 'app-chunk',
  templateUrl: './chunk.component.html',
  styleUrls: ['./chunk.component.scss'],
  standalone: false,
})
export class ChunkComponent implements OnInit, OnDestroy {
  private contentStateService = inject(ContentStateService);

  // --- Component Properties ---
  linkPrefix = environment.linkPrefix;
  initIsDone = false;
  jwReady = false;

  // --- Subscriptions ---
  private chunkModeSubscription: Subscription;
  private scrollEventSub: Subscription;
  private chunkUpdatedOnCacheSub: Subscription;
  private chunkStateSubscription: Subscription;
  private appCreateModeSub: Subscription;

  // --- View & Content Children ---
  @ViewChild('player', { static: false }) player: ElementRef;
  @ViewChild('editorWrapper') editorWrapper: ElementRef;

  // --- Inputs & Outputs ---
  chunkInput = input.required<any>({ alias: 'chunk' });
  highlightedChunk = this.contentStateService.highlightedChunkId;
  chunk = signal<any>(undefined);

  @Input() contentLanguage;
  @Input() mustLoadAudio;
  @Input() id;
  @Input() content;
  @Input() style;
  @Input() mode;
  @Input() index;
  @Input() contentID;
  @Input() editingChunk;
  @Input() videoWidth;
  @Input() nodelete;
  @Input() courseID;
  @Input() blogID;
  @Input() chunkAboveId;
  @Input() chunkBelowId;
  @Input() readOnly = false;

  @Output() audioAction = new EventEmitter<AudioActionEvent>();
  @Output() videoEnded = new EventEmitter<any>();
  @Output() chunkdeleted = new EventEmitter<any>();
  @Output() shouldReload = new EventEmitter<any>();
  @Output() editMe = new EventEmitter<any>();
  @Output() selectedStyle = new EventEmitter<any>();

  isTextual = computed(() => !['video', 'audio', 'image', 'file', 'quiz', 'canvas-transcript', 'code', 'whiteboard'].includes(this.chunk()?.type));
  isCode = computed(() => this.chunk()?.type === 'code');
  isMedia = computed(() => ['video', 'audio', 'image'].includes(this.chunk()?.type));
  isLoading = computed(() => {
    const loadingState = this.contentStateService.loading();
    return loadingState.status === 'loading' && loadingState.reason === 'fetch-data';
  });
  isPlaying = computed(() => {
    const playerState = this.contentStateService.playerState();
    return playerState.status === 'playing' && playerState.chunkId === this.chunk()?._id;
  });
  isCurrent = computed(() => {
    const playerState = this.contentStateService.playerState();
    const highlightedChunk = this.highlightedChunk();
    // if another chunk is being played or buffering, this one is not current
    if (playerState.chunkId && playerState.chunkId !== this.chunk()?._id) return false;
    // if no chunk is being played or buffering, check if this one is highlighted
    if (playerState.status !== 'playing' && playerState.status !== 'buffering') return highlightedChunk === this.chunk()?._id;
    // if this chunk is being played or buffering, it is current
    if (playerState.chunkId === this.chunk()?._id && (playerState.status === 'playing' || playerState.status === 'buffering')) return true;
    // otherwise,
    return false;
  });

  isGeneratingTask = computed(() => {
    const tasks = this.contentStateService.activeTasks();
    const chunkId = this.chunk()?._id;
    if (!chunkId || !tasks[chunkId]) return false;
    return Object.entries(tasks[chunkId]).some(
      ([taskType, task]: [string, any]) => taskType !== 'embedding' && task?.status === 'generating'
    );
  });

  isHovered = signal(false);
  clickEvent = signal<MouseEvent | null>(null);
  isMultiSelectMode = this.contentStateService.isMultiSelectMode;
  isSelected = computed(() => this.contentStateService.selectedChunks().includes(this.chunk()?._id));

  // --- State Properties ---
  storage;
  injector: Injector;
  isVideo;
  audio;
  observer;
  audioSpeed;
  appCreateMode = signal<boolean>(false);
  processingError = false;
  imgloaded = false;
  progress;
  loadingVideo = false;
  dropzoneHovered = false;
  showEditOption = true;
  generateVoice = false;
  selectedVoice = 'MALE';
  voices = [
    { value: 'MALE', viewValue: 'Homme' },
    { value: 'FEMALE', viewValue: 'Femme' },
    { value: 'NEUTRAL', viewValue: 'Neutre' },
  ];
  selectedVoiceLanguage = this.translate.currentLang;
  selectedLanguage = 'fr';
  languages = [
    { value: 'fr', viewValue: 'Français' },
    { value: 'en', viewValue: 'English' },
  ];
  chunkEditions = [];
  imageURL;
  playbackRate;
  videoIsPlaying;
  // editMode and dragMode properties are removed as they are no longer driven by a global service
  silentAudioDuration = 3;
  audioStatus;
  sanitizedHTML;
  voiceGenToast;
  translationToast;

  constructor(
    private appService: AppService,
    private _sanitizer: DomSanitizer,
    public cdService: CourseDisplayerService,
    private translate: TranslateService,
    public chunkService: ChunkService,
    private storageService: StorageService
  ) {
    effect(() => {
      // Re-run chunk initialization whenever chunkInput changes.
      const currentInputChunk = this.chunkInput();
      untracked(() => {
        if (currentInputChunk) {
          this.selectMainEdition(currentInputChunk)
            .then((success) => {
              if (!success || !this.chunk()) return;
              this.filterChunkEditions();
              this.isVideo = this.chunk().type == 'video' ? true : false;
              this.chunk.update((c) => ({
                ...c,
                currentTime: 0,
                iframeid: c._id + 'iframe',
                iframeidedit: c._id + 'iframeedit',
              }));
            })
            .catch((err) => {
              console.error(err);
            });
        }
      });
    });

    effect(() => {
      if (this.isPlaying()) {
        untracked(() => {
          this.setAsCurrent();
        });
      }
    });
    this.storage = inject(Storage);
    this.injector = inject(Injector);

    this.appCreateModeSub = this.appService.createMode$.subscribe((createMode) => {
      this.appCreateMode.set(createMode);
    });

    // Removed subscriptions to global editMode$ and dragMode$

    translate.onLangChange.subscribe((params) => {
      this.selectedLanguage = params.lang;
      this.loadAudio();
    });

    this.chunkModeSubscription = chunkService.currentMode$.subscribe((res: any) => {
      this.setMode(res.mode, res.id);
    });

    this.chunkUpdatedOnCacheSub = this.storageService.updatedCacheData$
      .pipe(
        takeUntilDestroyed(),
        filter((update) => update.table === 'chunks' && update.id[0] === this.chunk()?._id)
      )
      .subscribe(() => {
        this.storageService.find('chunks', [this.chunk()?._id]).then((chunk) => {
          this.selectMainEdition(chunk[0])
            .then((success) => {
              if (!success || !this.chunk()) return;
              this.filterChunkEditions();
              this.isVideo = this.chunk().type == 'video' ? true : false;
              this.chunk.update((c) => ({
                ...c,
                currentTime: 0,
                iframeid: c._id + 'iframe',
                iframeidedit: c._id + 'iframeedit',
              }));
            })
            .catch((err) => {
              console.error(err);
            });
        });
      });

    this.scrollEventSub = this.cdService.scrollEvent$.subscribe((res) => setTimeout(() => this.getChunkCoords(), 500));

    this.chunkStateSubscription = this.chunkService.chunkState$.subscribe((command: ChunkStateCommand) => {
      if (this.chunk()?._id === command.chunkId) {
        this.chunk.update((currentChunk) => ({
          ...currentChunk,
          editMode: command.editMode,
        }));
      }
    });
  }

  ngOnInit() {
    // Initialization moved to the effect in constructor
    // so that it reacts to input changes automatically.
  }

  ngOnDestroy() {
    this.scrollEventSub.unsubscribe();
    this.chunkModeSubscription?.unsubscribe();
    this.chunkUpdatedOnCacheSub?.unsubscribe();
    this.chunkStateSubscription?.unsubscribe();
    this.appCreateModeSub?.unsubscribe();
  }

  getChunkCoords() {
    const chunkElement = document.getElementById(this.chunk()?._id);
    if (chunkElement) {
      this.chunk.update((c) => ({
        ...c,
        ypos: chunkElement.getBoundingClientRect().top,
        bottompos: chunkElement.getBoundingClientRect().bottom,
      }));
    }
  }

  isString(element) {
    return typeof element === 'string';
  }

  async sleep(ms) {
    return await new Promise((resolve) => setTimeout(resolve, ms));
  }

  async uploadAudioSuccess() {
    await this.loadAudio(true);
    this.cdService.passAudioAction({ action: 'pause', content: this.chunk() });
  }

  filterChunkEditions() {
    const currentChunk = this.chunk();
    if (!currentChunk) return;

    currentChunk.editions.forEach((edition) => {
      if (edition?.type == 'html') {
        edition.sanitizedHTML = this._sanitizer.bypassSecurityTrustHtml(edition.content);
      }
    });

    if (this.selectedLanguage != 'all') {
      this.chunkEditions = currentChunk.editions.filter((e) => e && e.language == this.translate.currentLang);
    } else {
      this.chunkEditions = currentChunk.editions;
    }

    this.chunkEditions.forEach((edition) => {
      edition.isMain = currentChunk.mains.find((e) => e.main == edition._id) ? true : false;
    });
  }

  async selectMainEdition(content): Promise<boolean> {
    try {
      if (!content || !content.mains) return false;

      this.selectedLanguage = this.translate.currentLang;
      const mainID = content.mains.find((e) => e.language == this.selectedLanguage) || content.mains[0] || {};
      const mainEdition = content.editions.find((e) => mainID.main == e._id);

      let updatedChunk = { ...content, mainEdition: { ...mainEdition, type: content.type } };
      if (updatedChunk.status === 'processing') {
        updatedChunk.isPlaceHolder = true;
      }

      if (updatedChunk.mainEdition.type == 'html') {
        updatedChunk.mainEdition.sanitizedHTML = this._sanitizer.bypassSecurityTrustHtml(updatedChunk.mainEdition.content);
      }
      if (['video', 'image', 'audio', 'file', 'canvas-transcript'].includes(updatedChunk.mainEdition.type)) {
        if (updatedChunk.mainEdition.type == 'image') {
          const content = updatedChunk.mainEdition.content;
          if (content && !content.startsWith('https://')) {
            const storedItem = await this.storageService.db.images.get(content);
            if (storedItem && storedItem.blob) {
              const objectUrl = URL.createObjectURL(storedItem.blob);
              updatedChunk.imageURL = this._sanitizer.bypassSecurityTrustUrl(objectUrl);
              updatedChunk.objectUrl = objectUrl;
            }
          } else {
            const cachedImages: any = await this.storageService.find('images', [updatedChunk.mainEdition.content]);
            if (cachedImages.length) {
              updatedChunk.imageURL = this._sanitizer.bypassSecurityTrustResourceUrl('data:image/png;base64,' + cachedImages[0].data);
            } else {
              const downloadURL = await this.getDownloadURL(updatedChunk.mainEdition.content.replace('https://storage.googleapis.com/', 'gs://'));
              updatedChunk.imageURL = downloadURL;
              this.chunkService.getMediaData(updatedChunk.imageURL).subscribe(async (imageContent) => {
                await this.storageService.store('images', { _id: updatedChunk.mainEdition.content, data: imageContent });
              });
            }
          }
        } else if (updatedChunk.mainEdition.type == 'video') {
          updatedChunk.showEditOption = false;
          if (!updatedChunk.isPlaceHolder) {
            updatedChunk.video = this._sanitizer.bypassSecurityTrustUrl(
              await this.getDownloadURL(updatedChunk.mainEdition.content.replace('https://storage.googleapis.com/', 'gs://'))
            );
            updatedChunk.videoEdit = updatedChunk.video;
          }
        } else if (updatedChunk.mainEdition.type == 'audio') {
          updatedChunk.showEditOption = false;
          if (!updatedChunk.isPlaceHolder) {
            updatedChunk.audioContent = this._sanitizer.bypassSecurityTrustUrl(
              await this.getDownloadURL(updatedChunk.mainEdition.content.replace('https://storage.googleapis.com/', 'gs://'))
            );
          }
        }
      }

      this.chunk.set(updatedChunk);

      this.loadAudio();
      this.getChunkCoords();

      return true;
    } catch (err) {
      console.error('Error in selectMainEdition', err);
      return false;
    }
  }

  getDownloadURL(url): any {
    return new Promise((resolve, reject) => {
      runInInjectionContext(this.injector, () => {
        const reference = ref(this.storage, url);
        getDownloadURL(reference)
          .then((url) => resolve(url))
          .catch((err) => {
            console.error(err, '1st level');
            let filename = url.split(environment.publicBucketStorage)[1];
            url = `gs://${environment.publicBucketStorage}${filename}`;
            const storageRef = ref(this.storage, url);
            getDownloadURL(storageRef)
              .then((url) => resolve(url))
              .catch((err) => {
                console.error(err, '2nd level');
                resolve('images/cancel.png');
              });
          });
      });
    });
  }

  translateContent(piece): void {
    piece.editMode = 'translate';
  }

  filterLanguage(language) {
    this.filterChunkEditions();
  }

  deleteEdition(piece): void {
    this.chunkService.deleteChunkEdition({ id: this.chunk()?._id, editionId: piece._id }).subscribe((res) => this.handleEditionManipulationSuccess(res, 'deleteEdition'));
  }

  handleEditionManipulationSuccess(res, reqName?): void {
    // this.selectMainEdition(res.data);
    // this.chunk.set(res.data);
    // this.filterChunkEditions();
    // if (reqName == 'deleteEdition') {
    //   this.chunk.update((c) => ({ ...c, editMode: 'seeEdition' }));
    // }
  }

  deleteUpdate(piece): void {
    piece.edition = '';
  }

  loadAudio(forced = false) {
    return new Promise(async (resolve, reject) => {
      const currentChunk = this.chunk();
      if (currentChunk) {
        if ((!currentChunk.audio && currentChunk.hasAudio) || forced) {
          for (const audio of currentChunk.audios) {
            if (audio._id == this.translate.currentLang) {
              const audioUrl = await this.getDownloadURL(audio.fileLink);
              const link = {
                url: audioUrl,
                duration: audio.duration,
                language: this.translate.currentLang,
                durationSet: audio.audioDurationToBeSet || true,
              };
              this.chunk.update((c) => ({
                ...c,
                audio: this._sanitizer.bypassSecurityTrustResourceUrl(link.url),
                hasSilentAudio: false,
                audioDuration: link.duration,
                audioLanguage: link.language,
              }));
              resolve({});
              break;
            }
          }
        }
      }
    });
  }

  handleAudioVideoEnded(e) {
    this.videoEnded.emit({
      event: 'videoEnded',
      chunkID: this.chunk()?._id,
    });
  }

  setAsCurrent(event?: MouseEvent) {
    if (this.contentStateService.isLiveTranscribing()) return;

    if (event) {
      this.clickEvent.set(event);
    }
    this.contentStateService.setHighlightedChunkId(this.chunk()?._id);
    if (this.contentStateService.playerState().status !== 'playing') {
      this.contentStateService.clearAudio();
    }
  }

  setMode(mode, id) {
    if (id == this.chunk()?._id) {
      this.editMe.emit(this.chunk()?._id);
      this.mode = mode;
    }
  }

  emitPlayAction(event: Chunk | null) {
    const chunk = event || this.chunk();
    this.contentStateService.playTrack(chunk?._id, chunk);
  }

  emitPauseAction() {
    this.contentStateService.pauseAudio();
  }

  onDoneEditing() {
    this.editMe.emit(this.chunk()._id);
  }
}
